import React, { useState } from 'react';
import PropTypes from 'prop-types';
import './SolutionsModal.css';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faMeteor,
  faInfoCircle,
  faNewspaper,
  faProjectDiagram,
  faBell,
  faBullhorn,
} from '@fortawesome/free-solid-svg-icons';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from 'recharts';

// Traducciones del modal
const modalTranslations5 = {
  es: {
    header: 'Identifica y mitiga los riesgos con datos en tiempo real',
    intro:
      'Toma decisiones proactivas de talento de tu organización. Con TalentPanel®, Alertas Inteligentes y Gestión de Campañas tendrás un control total sobre tu capital humano, minimizando riesgos y alineando tu fuerza de trabajo con las prioridades del negocio.',
    sections: [
      {
        icon: faProjectDiagram,
        title: 'TalentPanel®: Visión Integral de tu Empresa',
        bullets: [
          'Mapa de equipos y roles: muestra la salud de cada departamento, combinando indicadores de desempeño, potencial y riesgo de rotación.',
          'Dashboard configurable: elige métricas clave (bench strength, eNPS, gaps de habilidades…) para ver donde más importa.',
          'Comparativa “best vs. underperforming”: detecta rápidamente qué unidades superan expectativas y cuáles necesitan apoyo inmediato.',
        ],
      },
      {
        icon: faBell,
        title: 'Alertas Inteligentes',
        bullets: [
          'Notificaciones tempranas: recibe avisos cuando la probabilidad de rotación o el gap de habilidades supera umbrales críticos.',
          'Análisis de impacto: cuantifica el coste y la repercusión en proyectos clave de cada posible salida de talento.',
          'Recomendaciones de acción: la IA de Bether sugiere intervenciones específicas (mentoring, reasignación de tareas, revisiones salariales).',
        ],
      },
      {
        icon: faBullhorn,
        title: 'Gestión de Campañas',
        bullets: [
          'Lanzamiento ágil: diseña y despliega rondas de evaluación y planes de desarrollo en unos clics.',
          'Seguimiento centralizado: controla el estado de cada campaña, participantes y resultados desde un único panel.',
          'Automatización de recordatorios: programa envíos de encuestas y revisiones periódicas para maximizar la tasa de respuesta y la regularidad.',
        ],
      },
    ],
    benefitsText:
      'Mira todos los beneficios que puedes obtener al poner en práctica una estrategia como esta:',
    sourceLabel: 'Fuente',
    kpis: [
      {
        metric: 'ROI de Formación & Desarrollo',
        baseline: 100,
        improved: 200,
        unit: '%',
        center: '×2',
        tooltipText:
          'Al priorizar los programas con mayor impacto y vincular el avance de competencias a indicadores de negocio (ventas, calidad, plazos), Bether convierte cada euro en el doble de valor.',
        link: 'https://www.continu.com/blog/training-roi',
      },
      {
        metric: 'Time to Fill',
        baseline: 44,
        improved: 22,
        unit: 'days',
        center: '-50%',
        tooltipText:
          'Con los BetherPlan® automatizados, aceleras tanto el tiempo de cobertura de vacantes (–50 %) como el tiempo que tarda un empleado a tener el rendimiento pleno (–30 %).',
        link: 'https://employmenthero.com/blog/time-to-hire',
      },
      {
        metric: 'Time to Productivity',
        baseline: 35,
        improved: 24.5,
        unit: 'days',
        center: '-30%',
        tooltipText:
          'Con los BetherPlan® automatizados, aceleras tanto el tiempo de cobertura de vacantes (–50 %) como el tiempo que tarda un empleado a tener el rendimiento pleno (–30 %).',
        link: 'https://hrexecutive.com/5-ways-to-strengthen-your-onboarding-and-boost-time-to-productivity',
      },
    ],
  },
  en: {
    header: 'Identify and Mitigate Risks with Real-Time Data',
    intro:
      'Make proactive talent decisions for your organization. With TalentPanel®, Intelligent Alerts, and Campaign Management you’ll have total control over your human capital, minimizing risks and aligning your workforce with business priorities.',
    sections: [
      {
        icon: faProjectDiagram,
        title: 'TalentPanel®: Holistic Enterprise View',
        bullets: [
          'Team & role map: shows each department’s health, combining performance, potential, and attrition-risk indicators.',
          'Configurable dashboard: pick key metrics (bench strength, eNPS, skills gaps…) to see what matters most.',
          'Best vs. underperforming comparison: quickly spot units that exceed expectations and those needing support.',
        ],
      },
      {
        icon: faBell,
        title: 'Intelligent Alerts',
        bullets: [
          'Early notifications: get alerts when attrition risk or skills gap crosses critical thresholds.',
          'Impact analysis: quantify the cost and effect on key projects of any potential talent loss.',
          'Action recommendations: Bether’s AI suggests targeted interventions (mentoring, task reassignment, pay reviews).',
        ],
      },
      {
        icon: faBullhorn,
        title: 'Campaign Management',
        bullets: [
          'Agile launch: design and roll out assessment rounds and development plans in a few clicks.',
          'Centralized tracking: monitor campaign status, participants, and outcomes from one panel.',
          'Automated reminders: schedule surveys and periodic reviews to maximize response rates and consistency.',
        ],
      },
    ],
    benefitsText:
      'See all the benefits you can gain by implementing a strategy like this:',
    sourceLabel: 'Source',
    kpis: [
      {
        metric: 'Training & Development ROI',
        baseline: 100,
        improved: 200,
        unit: '%',
        center: '×2',
        tooltipText:
          'By prioritizing high-impact programs and tying skill progress to business KPIs (sales, quality, deadlines), Bether doubles the value of every euro spent.',
        link: 'https://www.continu.com/blog/training-roi',
      },
      {
        metric: 'Time to Fill',
        baseline: 44,
        improved: 22,
        unit: 'days',
        center: '-50%',
        tooltipText:
          'With automated BetherPlan®, you halve vacancy fill time (–50 %) and speed up time to full performance (–30 %).',
        link: 'https://employmenthero.com/blog/time-to-hire',
      },
      {
        metric: 'Time to Productivity',
        baseline: 35,
        improved: 24.5,
        unit: 'days',
        center: '-30%',
        tooltipText:
          'With automated BetherPlan®, you halve vacancy fill time (–50 %) and speed up time to full performance (–30 %).',
        link: 'https://hrexecutive.com/5-ways-to-strengthen-your-onboarding-and-boost-time-to-productivity',
      },
    ],
  },
};

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const { name, value, payload: data } = payload[0];
    const formatted =
      data.unit === 'days' ? Math.round(value) : value.toFixed(1);
    return (
      <div className="tooltip-box">
        <strong>{name}</strong>: {formatted}
        {data.unit}
      </div>
    );
  }
  return null;
};

const KPICharts = ({ kpis, sourceLabel }) => {
  const [infoVisible, setInfoVisible] = useState({});
  const toggleInfo = (metric) =>
    setInfoVisible((prev) => ({ ...prev, [metric]: !prev[metric] }));

  return (
    <div className="smodal-tei-charts">
      {kpis.map(
        ({ metric, baseline, improved, unit, center, tooltipText, link }) => {
          const maxValue =
            unit === '%' && improved > 100 ? improved : unit === '/5' ? 5 : 100;
          const outerData = [
            { name: 'Bether Average', value: improved, unit },
            { name: '', value: maxValue - improved },
          ];
          const innerData = [
            { name: 'Business Average', value: baseline, unit },
            { name: '', value: maxValue - baseline },
          ];

          return (
            <div key={metric} className="smodal-tei-chart-card">
              <h4 className="smodal-tei-chart-title">{metric}</h4>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={outerData}
                    dataKey="value"
                    nameKey="name"
                    startAngle={90}
                    endAngle={-270}
                    innerRadius="65%"
                    outerRadius="95%"
                    paddingAngle={2}
                    isAnimationActive={false}
                  >
                    {outerData.map((_, idx) => (
                      <Cell
                        key={idx}
                        fill={idx === 0 ? '#ff2300' : '#e0e0e0'}
                      />
                    ))}
                  </Pie>
                  <Pie
                    data={innerData}
                    dataKey="value"
                    nameKey="name"
                    startAngle={90}
                    endAngle={-270}
                    innerRadius="45%"
                    outerRadius="63%"
                    paddingAngle={2}
                    isAnimationActive={false}
                  >
                    {innerData.map((_, idx) => (
                      <Cell
                        key={idx}
                        fill={idx === 0 ? '#555555' : '#e0e0e0'}
                      />
                    ))}
                  </Pie>
                  <text
                    x="50%"
                    y="50%"
                    textAnchor="middle"
                    dominantBaseline="middle"
                    className="smodal-tei-chart-center"
                  >
                    {center}
                  </text>
                  <RechartsTooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
              <FontAwesomeIcon
                icon={faInfoCircle}
                className="info-icon"
                onClick={() => toggleInfo(metric)}
              />
              {infoVisible[metric] && (
                <div className="tooltip-popup">
                  <div className="tooltip-text">{tooltipText}</div>
                  {link && (
                    <button
                      className="source-button"
                      onClick={() => window.open(link, '_blank')}
                    >
                      <FontAwesomeIcon icon={faNewspaper} />
                      &nbsp;{sourceLabel}
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        }
      )}
    </div>
  );
};

const SolutionsModal5 = ({ isOpen, onClose, language = 'es' }) => {
  if (!isOpen) return null;
  const t = modalTranslations5[language];

  return (
    <div className="smodal-overlay">
      <div className="smodal-content">
        <button className="smodal-close" onClick={onClose}>
          <FontAwesomeIcon icon={faTimes} />
        </button>
        <h2 className="smodal-header">{t.header}</h2>
        <p className="smodal-tei-intro">{t.intro}</p>

        {t.sections.map((sec, idx) => (
          <div key={idx} className="smodal-section">
            <FontAwesomeIcon icon={sec.icon} className="section-icon" />
            <div className="section-content">
              <h3 className="section-title">{sec.title}</h3>
              {sec.bullets.length > 0 && (
                <ul className="section-list">
                  {sec.bullets.map((b, i) => (
                    <li key={i}>{b}</li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        ))}

        <div className="smodal-tei-box">
          <h3 className="smodal-tei-title">
            <FontAwesomeIcon icon={faMeteor} className="tei-icon" /> TEI: Total
            Economic Impact
          </h3>
          <p className="smodal-tei-intro">{t.benefitsText}</p>
          <KPICharts kpis={t.kpis} sourceLabel={t.sourceLabel} />
        </div>
      </div>
    </div>
  );
};

SolutionsModal5.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  language: PropTypes.oneOf(['es', 'en']),
};

export default SolutionsModal5;
